import React from 'react';
import * as Headless from '@headlessui/react';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { useLoaderData, NavLink } from 'react-router-dom';
import {
  AccountBusinessPolicyFieldsFragment,
  AccountFieldsFragment,
  BusinessPolicyDetailFieldsFragment,
  BusinessPolicyFieldsFragment,
  BusinessPolicyFileFieldsFragment,
  PolicyState
} from '../api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import { Button } from '@oysterjs/ui/button';
import { ArrowRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { getDisplayInsuranceType } from '../api/map';
import { Badge } from '@oysterjs/ui/badge';

export const PoliciesPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          account: AccountFieldsFragment & AccountBusinessPolicyFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <TransitionDiv className="my-16 flex items-center justify-center">
          <div className="rounded-lg bg-white p-10 shadow">
            <Spinner size="size-10" color="neutral" />
          </div>
        </TransitionDiv>
      }
    >
      <Await resolve={loader?.data}>
        {(data) =>
          data && (
            <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6">
              <h2 className="text-xl font-semibold mt-0 mb-2">Your Policies</h2>
              <Policies policies={data.account.businessPolicies} />
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const Policies = ({ policies }: { policies: BusinessPolicyFieldsFragment[] }) => {
  if (policies.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No policies found. Your agency hasn't added any policies yet. When they do, you'll be able
        to view them here.
      </p>
    );
  }

  return (
    <ul role="list" className="divide-y divide-gray-100 mt-4">
      {policies.map((policy) => (
        <li key={policy.id} className="py-3 first:pt-0 last:pb-0">
          <div>
            <div className="flex items-start gap-x-3">
              <NavLink
                to={`/policies/${policy.id}`}
                className="text-base/6 font-medium text-neutral-900 hover:underline"
              >
                {getDisplayInsuranceType(policy.type)}
              </NavLink>
              <Badge
                // eslint-disable-next-line
                // @ts-ignore
                color={
                  {
                    [PolicyState.Pending]: 'amber',
                    [PolicyState.Binding]: 'sky',
                    [PolicyState.Inforce]: 'green',
                    [PolicyState.Expired]: 'neutral',
                    [PolicyState.Canceled]: 'rose'
                  }[policy.state] || 'neutral'
                }
              >
                {policy.state}
              </Badge>
            </div>
            <div className="flex items-center gap-x-2 text-neutral-500">
              <p className="truncate text-sm">{policy.carrier.name}</p>
              <svg viewBox="0 0 2 2" className="size-0.5 fill-current">
                <circle r={1} cx={1} cy={1} />
              </svg>
              {policy.inforceAt && (
                <p className="whitespace-nowrap text-sm">
                  Effective{' '}
                  <time dateTime={new Date(policy.inforceAt).toISOString()}>
                    {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                      new Date(policy.inforceAt)
                    )}
                  </time>
                </p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const PolicyPage = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      businessPolicy: BusinessPolicyFieldsFragment &
        BusinessPolicyDetailFieldsFragment &
        BusinessPolicyFileFieldsFragment;
    }>;
  };

  return (
    <React.Suspense
      fallback={
        <TransitionDiv className="my-16 flex items-center justify-center">
          <div className="rounded-lg bg-white p-10 shadow">
            <Spinner size="size-10" color="neutral" />
          </div>
        </TransitionDiv>
      }
    >
      <Await resolve={loader.data}>
        {({ businessPolicy }) => <PolicyExplorer policy={businessPolicy} />}
      </Await>
    </React.Suspense>
  );
};

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const PolicyExplorer = (props: {
  policy: BusinessPolicyFieldsFragment &
    BusinessPolicyDetailFieldsFragment &
    BusinessPolicyFileFieldsFragment;
}) => {
  const selectedPolicy = props.policy;
  const [showPolicyQuotes, setShowPolicyQuotes] = React.useState<Record<string, boolean>>({});

  const policyStats = [
    {
      name: 'Premium',
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        selectedPolicy.pricing.premium || 0
      )
    },
    {
      name: 'Policy Number',
      value: selectedPolicy.policyNumber || selectedPolicy.details?.policyNumber || 'N/A'
    },
    {
      name: 'Expiration',
      value: new Date(selectedPolicy.expiresAt || '').toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    }
  ];

  const policyLimits = [
    { name: 'aggregate', limit: selectedPolicy.details?.limits?.cyberAggregateLimit },
    { name: 'retention', limit: selectedPolicy.details?.limits?.cyberRetentionLimit },
    { name: 'aggregate', limit: selectedPolicy.details?.limits?.glAggregateLimit },
    {
      name: 'medical per occurrence',
      limit: selectedPolicy.details?.limits?.glMedicalPerOccurrenceLimit
    },
    {
      name: 'medical per person',
      limit: selectedPolicy.details?.limits?.glMedicalPerPersonLimit
    },
    { name: 'per occurrence', limit: selectedPolicy.details?.limits?.glPerOccurrenceLimit },
    {
      name: 'personal and advertising injury',
      limit: selectedPolicy.details?.limits?.glPersonalAndAdvertisingInjuryLimit
    },
    {
      name: 'products and completed operations',
      limit: selectedPolicy.details?.limits?.glProductsAndCompletedOperationsLimit
    },
    {
      name: 'rented premises damage',
      limit: selectedPolicy.details?.limits?.glRentedPremisesDamageLimit
    },
    { name: 'building', limit: selectedPolicy.details?.limits?.propertyBuildingLimit },
    { name: 'contents', limit: selectedPolicy.details?.limits?.propertyContentsLimit },
    { name: 'per accident', limit: selectedPolicy.details?.limits?.wcPerAccidentLimit },
    {
      name: 'per disease (employee)',
      limit: selectedPolicy.details?.limits?.wcPerDiseaseEmployeeLimit
    },
    {
      name: 'per disease (policy)',
      limit: selectedPolicy.details?.limits?.wcPerDiseasePolicyLimit
    },
    ...(selectedPolicy.details?.extraLimits || []),
    ...(selectedPolicy.details?.locations?.flatMap((location) => [
      {
        name: `building (${location.address.line1})`,
        limit: location.limits?.propertyBuildingLimit
      },
      {
        name: `contents (${location.address.line1})`,
        limit: location.limits?.propertyContentsLimit
      }
    ]) || [])
  ].filter((limit) => !!limit?.limit);

  return (
    <>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="flex flex-col gap-8 lg:col-span-1">
          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-xl font-semibold mt-0 mb-2">Overview</h2>
              <p className="text-sm text-neutral-500">Basic details about this policy.</p>
            </div>
            <div className="@container">
              <dl className="px-4 @md:py-5 py-3 sm:px-6 mx-auto grid grid-cols-1 gap-4 @md:grid-cols-3">
                {policyStats.map((stat) => (
                  <div
                    key={stat.name}
                    className="flex flex-wrap items-baseline justify-between bg-white"
                  >
                    <dt className="text-sm font-medium text-neutral-500">{stat.name}</dt>
                    <dd className="w-full flex-none text-xl font-medium leading-10 tracking-tight text-neutral-900">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <dl className="divide-y divide-neutral-100 border-t border-neutral-100 pb-2">
                <div className="px-4 py-3 @md:grid @md:grid-cols-3 @md:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Insured Entity</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 @md:col-span-2 @md:mt-0">
                    <span className="font-medium">
                      {selectedPolicy.details?.insured?.name || 'Unknown'}
                    </span>
                    <br />
                    <span>{selectedPolicy.details?.insured?.address?.line1 || 'Unknown'}</span>
                    <br />
                    <span>
                      {selectedPolicy.details?.insured?.address?.city || 'Unknown'},{' '}
                      {selectedPolicy.details?.insured?.address?.zone || 'Unknown'}{' '}
                      {selectedPolicy.details?.insured?.address?.postalCode || 'Unknown'}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-3 @md:grid @md:grid-cols-3 @md:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Carrier</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 @md:col-span-2 @md:mt-0">
                    <span className="font-medium">
                      {selectedPolicy.details?.carrier?.name || 'Unknown'}
                    </span>
                    <br />
                    <span>{selectedPolicy.details?.carrier?.address?.line1 || 'Unknown'}</span>
                    <br />
                    <span>
                      {selectedPolicy.details?.carrier?.address?.city || 'Unknown'},{' '}
                      {selectedPolicy.details?.carrier?.address?.zone || 'Unknown'}{' '}
                      {selectedPolicy.details?.carrier?.address?.postalCode || 'Unknown'}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-3 @md:grid @md:grid-cols-3 @md:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Policy Summary</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 @md:col-span-2 @md:mt-0">
                    {selectedPolicy.details?.summary || 'Unknown'}
                  </dd>
                </div>
                <div className="px-4 py-3 @md:grid @md:grid-cols-3 @md:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Limits</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 @md:col-span-2 @md:mt-0">
                    {policyLimits.map((limit) => (
                      <div key={limit.name}>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                          currency: 'USD'
                        }).format(limit.limit || 0)}{' '}
                        {limit.name}
                      </div>
                    ))}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-xl font-semibold mt-0 mb-2">Documents</h2>
              <p className="text-sm text-neutral-500">Documents related to this policy.</p>
            </div>
            <div className="@container px-4 py-5 sm:px-6">
              <ul role="list" className="flex flex-col gap-4">
                {selectedPolicy.files.map((file) => (
                  <li key={file.id} className="flex gap-x-2 gap-y-4">
                    <div className="flex-grow">
                      <p className="text-sm font-medium text-neutral-900">{titleCase(file.type)}</p>
                      <p className="text-sm text-neutral-500">{file.name}</p>
                    </div>
                    <div>
                      <Button plain href={file.url || '#'} target="_blank">
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-8 lg:col-span-2">
          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-xl font-semibold mt-0 mb-2">What's covered</h2>
              <p className="text-sm text-neutral-500">Coverage included on this policy.</p>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col gap-2">
                <dl className="grid grid-cols-1 gap-4">
                  {selectedPolicy.details?.coverageDescriptions?.map((coverage) => (
                    <div key={coverage.title}>
                      <dt className="text-sm flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden="true" />
                        <span className="font-medium text-neutral-500">{coverage.title}</span>
                      </dt>
                      <dd className="text-sm text-neutral-900 ml-6 mt-1 leading-5">
                        {coverage.description}
                      </dd>
                      {coverage.policyQuotes.length > 0 && !showPolicyQuotes[coverage.title] && (
                        <Headless.Button
                          onClick={() =>
                            setShowPolicyQuotes({
                              ...showPolicyQuotes,
                              [coverage.title]: true
                            })
                          }
                          className="text-xs font-medium text-primary-500 hover:text-primary-800 inline-flex items-center gap-1 ml-6"
                        >
                          Show relevant quotes <ArrowRightIcon className="h-3 w-3" />
                        </Headless.Button>
                      )}
                      {coverage.policyQuotes.length > 0 && showPolicyQuotes[coverage.title] && (
                        <div className="flex flex-col gap-2 mt-2 ml-6">
                          {coverage.policyQuotes.map((quote) => (
                            <div
                              key={quote}
                              className="text-xs text-neutral-500 border-l-4 border-neutral-200 px-2 py-0.5"
                            >
                              {quote}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-neutral-400 mt-2">
                  The coverage descriptions provided here may not be complete, and may have
                  important exclusions or limitations. Please read the full policy document to
                  confirm the information here.
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-xl font-semibold mt-0 mb-2">What's not covered</h2>
              <p className="text-sm text-neutral-500">
                Exclusions and other limitations on this policy.
              </p>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col gap-2">
                <dl className="grid grid-cols-1 gap-4">
                  {selectedPolicy.details?.exclusionDescriptions?.map((exclusion) => (
                    <div key={exclusion.title}>
                      <dt className="text-sm flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
                        <span className="font-medium text-neutral-500">{exclusion.title}</span>
                      </dt>
                      <dd className="text-sm text-neutral-900 ml-6 mt-1 leading-5">
                        {exclusion.description}
                      </dd>
                      {exclusion.policyQuotes.length > 0 && !showPolicyQuotes[exclusion.title] && (
                        <Headless.Button
                          onClick={() =>
                            setShowPolicyQuotes({
                              ...showPolicyQuotes,
                              [exclusion.title]: true
                            })
                          }
                          className="text-xs font-medium text-primary-500 hover:text-primary-800 inline-flex items-center gap-1 ml-6"
                        >
                          Show relevant quotes <ArrowRightIcon className="h-3 w-3" />
                        </Headless.Button>
                      )}
                      {exclusion.policyQuotes.length > 0 && showPolicyQuotes[exclusion.title] && (
                        <div className="flex flex-col gap-2 mt-2 ml-6">
                          {exclusion.policyQuotes.map((quote) => (
                            <div
                              key={quote}
                              className="text-xs text-neutral-500 border-l-4 border-neutral-200 px-2 py-0.5"
                            >
                              {quote}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-neutral-400 mt-2">
                  The exclusions provided here may not be complete, and may have important caveats
                  or conditions. Please read the full policy document to confirm the information
                  here.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
